import React, { useState, useMemo } from 'react';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';

import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import TextField from '@material-ui/core/TextField';
import Switch from '@material-ui/core/Switch';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Collapse from '@material-ui/core/Collapse';
import Checkbox from '@material-ui/core/Checkbox';
import FormGroup from '@material-ui/core/FormGroup';

import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemSecondaryAction from '@material-ui/core/ListItemSecondaryAction';

const useListItemStyles = makeStyles(theme => ({
    nested: props => ({
        paddingLeft: (10 + props.depth * 14),
    }),
    listItemIcon: {
        minWidth: 0
    }
}));

const findChildIds = (children) => {

    let ids = [];

    children.forEach(c => {

        ids.push(c.id);

        if (c.children_with_count.length > 0) {
            ids = ids.concat(findChildIds(c.children_with_count));
        }
    })

    return ids;
}

const NestedLevelItem = (props) => {

    const { level, depth, levelsChecked, onCheck, onCheckChildren } = props;

    const classes = useListItemStyles({ depth: depth});

    const [ includeChildren, setIncludeChildren ] = useState(false);

    const isSelected = levelsChecked.indexOf(level.id) > -1 ;

    const childIds = (level.children_with_count.length === 0) ? 0 : useMemo(() => findChildIds(level.children_with_count), []);

    const handleIncludeChildren = () => {
        setIncludeChildren(! includeChildren);
        const ids = [...childIds];
        if (! includeChildren) ids.push(level.id);
        onCheckChildren(! includeChildren, ids);
    }

    return (
        <>
            <ListItem
                classes={{ root: classes.nested }}
                button
                dense
                onClick={() => onCheck(level.id)}
                >
                <ListItemIcon classes={{ root: classes.listItemIcon }}>
                    <Checkbox
                        edge="start"
                        checked={isSelected}
                        tabIndex={-1}
                        disableRipple
                        color="primary"
                    />
                </ListItemIcon>
                <ListItemText
                    primary={ level.title }
                    className={ classes.listItemText }
                />
                <ListItemSecondaryAction>
                    <Typography
                        display="inline"
                        color="textSecondary"
                        variant="caption"
                        >
                        Incl. Sub
                    </Typography>
                    <Switch
                        edge="end"
                        onChange={handleIncludeChildren}
                        checked={includeChildren}
                        disabled={level.children_with_count.length === 0}
                        color="primary"
                    />
                </ListItemSecondaryAction>
            </ListItem>
            {level.children_with_count.map((child) => (
                <NestedLevelItem
                    key={child.id}
                    level={child}
                    depth={(depth + 1)}
                    levelsChecked={levelsChecked}
                    onCheck={onCheck}
                    onCheckChildren={onCheckChildren}
                />
            ))}
        </>
    )
}

const useStyles = makeStyles(theme => ({
    section: {
        marginBottom: theme.spacing(3)
    },
    formContainer: {
        overflowY: 'scroll',
        height: '100%',
        padding: theme.spacing(3)
    },
    mobileContainer: {
        height: '100%',
        padding: theme.spacing(3)
    },
    image: {
        maxHeight: 300,
        maxWidth: 300,
        height: 'auto',
        width: '100%'
    },
    imageContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100%',
        padding: theme.spacing(6)
    },
    height: {
        height: '100%'
    },
    opacity: {
        opacity: 0.5
    },
    marginBottomSmall: {
        marginBottom: theme.spacing(2)
    },
    subSection: {
        padding: theme.spacing(1),
    },
    subSectionLoading: {
        textAlign: 'center',
        padding: theme.spacing(3),
    },
    noMarginLeft: {
        marginLeft: 0
    },
    empty: {
        paddingTop: theme.spacing(3),
        paddingBottom: theme.spacing(3),
    },
    noContentPadding: {
        padding: 0
    },
    groupSelect: {
        backgroundColor: theme.palette.grey[200],
        borderRadius: theme.shape.borderRadius,
        marginBottom: theme.spacing(3),
        maxWidth: 400,
        maxHeight: 400,
        overflowY: 'scroll'
    }
}));

export default function Invitations(props) {

    const {
        event,
        groups,
        levels,
        groupsChecked,
        handleGroup,
        handleLevelChildren,
        handleEvent,
        isError,
        getErrorMessage,
        advancedOpen,
        setAdvancedOpen,
        newEvent
    } = props;

    const classes = useStyles();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const getDatesAllowedText = () => {
        if (isError('dates_allowed')) return getErrorMessage('dates_allowed');
        return "Allows guests to add dates to their invitation. Is overridden by a ticket's number of dates allowed if invitation is attached to a ticket."
    }

    const getInvitationTitleText = () => {
        if (isError('invitation_title')) return getErrorMessage('invitation_title');
        return "This will be used for auto-generated invitations and other places to help expedite the invitation process."
    }

    const getGuestsAllowedText = () => {
        if (isError('guests_allowed')) return getErrorMessage('guests_allowed');
        return "Set to 0 to allow group guests to have unlimited invitations."
    }

    return (
        <Grid container direction="row" className={newEvent ? classes.height : null}>
            {!newEvent ? null : isMobile ? null :
                <Grid item xs className={classes.height}>
                    <div className={classes.imageContainer}>
                        <img className={classes.image} src='/images/step-voting.png' alt="event-graphic" />
                    </div>
                </Grid>
            }
            <Grid item xs={isMobile ? 12 : newEvent ? 7 : 12} className={isMobile ? classes.mobileContainer : newEvent ? classes.formContainer : null}>
                <Typography variant="h6" gutterBottom>
                    Invitations
                </Typography>
                {newEvent &&
                    <div className={classes.section}>
                        <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                            Select Groups who are Invited to the Event. You can always add, edit and remove invitations by user or group later on.
                        </Typography>
                        <div className={classes.groupSelect}>
                            {(groups.length > 0) && (
                                <FormGroup className={classes.subSection}>
                                    {groups.map(group => (
                                        <FormControlLabel
                                            className={classes.noMarginLeft}
                                            key={group.id}
                                            control={
                                                <Checkbox
                                                    checked={(groupsChecked.indexOf(group.id) > -1)}
                                                    onChange={() => handleGroup(group.id)}
                                                    name={group.title}
                                                    color="primary"
                                                    size="small"
                                                />
                                            }
                                            label={group.title}
                                        />
                                    ))}
                                </FormGroup>
                            )}
                            {(levels.length > 0) && (
                                <List className={classes.noContentPadding}>
                                    {levels.map(level => (
                                        <NestedLevelItem
                                            key={level.id}
                                            level={level}
                                            depth={1}
                                            levelsChecked={groupsChecked}
                                            onCheck={handleGroup}
                                            onCheckChildren={handleLevelChildren}
                                        />
                                    ))}
                                </List>
                            )}
                            {(groups.length == 0 && levels.length == 0) && (
                                <div className={classes.subSection}>
                                    <Typography
                                        variant="subtitle2"
                                        color="textSecondary"
                                        className={classes.empty}
                                        align="center"
                                        >
                                        No Available Groups
                                    </Typography>
                                </div>
                            )}
                        </div>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={event.rsvp}
                                    onChange={e => handleEvent('rsvp', e)}
                                    name="rsvp"
                                    color="primary"
                                />
                            }
                            label="Allow users to RSVP?"
                        />
                        <br/>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={event.addSpouse}
                                    onChange={e => handleEvent('addSpouse', e)}
                                    color="primary"
                                    name="addSpouse"
                                />
                            }
                            label="Would you like to add a user's spouse to the invitation as their date if possible?"
                        />
                        <br/>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={event.emailInvitation}
                                    onChange={e => handleEvent('emailInvitation', e)}
                                    color="primary"
                                    name="flag"
                                />
                            }
                            label={`Would you like to email invitations to users?`}
                            labelPlacement="end"
                            margin="normal"
                        />
                        <br/><br/>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={advancedOpen}
                                    onChange={() => setAdvancedOpen(!advancedOpen)}
                                    color="primary"
                                />
                            }
                            label="View Advanced Options"
                        />
                    </div>
                }
                <Collapse in={advancedOpen || !newEvent}>
                    <div className={classes.section}>
                        <Typography variant="subtitle1" gutterBottom color="primary">
                            Invitations (General Settings)
                        </Typography>
                        {(! newEvent) &&
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={event.rsvp}
                                        onChange={e => handleEvent('rsvp', e)}
                                        name="rsvp"
                                        color="primary"
                                    />
                                }
                                label="Allow guests to RSVP?"
                                margin="normal"
                            />
                        }
                        <TextField
                            id="input-invitation-title"
                            label="Default Invitation Title"
                            value={event.invitation_title}
                            margin="normal"
                            onChange={e => handleEvent('invitation_title', e)}
                            fullWidth
                            autoFocus
                            error={isError('invitation_title')}
                            helperText={getInvitationTitleText()}
                            variant="outlined"
                            placeholder={`You have been invited to ${event.name}!`}
                        />
                        <TextField
                            label="Number of Dates Allowed per Invitation?"
                            id="input-dates-allowed"
                            type="number"
                            value={event.dates_allowed}
                            onChange={e => handleEvent('dates_allowed', e)}
                            variant="outlined"
                            fullWidth
                            error={isError('dates_allowed')}
                            margin="normal"
                            helperText={getDatesAllowedText()}
                        />
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={event.flag_outstanding}
                                    onChange={e => handleEvent('flag_outstanding', e)}
                                    color="primary"
                                    value="flag_outstanding"
                                />
                            }
                            label="Flag guests who have unpaid invoices/membership dues? (Guests will be allowed to pay upon entry)"
                            labelPlacement="end"
                            margin="normal"
                        />
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={event.display_snailmail}
                                    onChange={e => handleEvent('display_snailmail', e)}
                                    name="display_snailmail"
                                    color="primary"
                                />
                            }
                            label="When inviting a guest, should we ask if we should mail an invitation?"
                            margin="normal"
                        />
                    </div>
                    <div className={classes.section}>
                        <Typography variant="subtitle1" gutterBottom color="primary">
                            Invitations (Group Settings)
                        </Typography>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={event.group_tickets_required}
                                    onChange={e => handleEvent('group_tickets_required', e)}
                                    color="primary"
                                    value="group_tickets_required"
                                    // disabled={event.public}
                                />
                            }
                            label='Are guests invited by a group required to have tickets to this event? (i.e. "Members Group")'
                            labelPlacement="end"
                            margin="normal"
                        />
                    </div>
                    <div className={classes.section}>
                        <Typography variant="subtitle1" color="primary" gutterBottom>
                            Invitations (Non-Group Settings)
                        </Typography>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={event.invitations_active}
                                    onChange={e => handleEvent('invitations_active', e)}
                                    color="primary"
                                    value="invitations_active"
                                />
                            }
                            label='Can guests invited by a group send invitations to guests outside the group? (i.e. "Non-Members")'
                            labelPlacement="end"
                            margin="normal"
                            className={classes.marginBottomSmall}
                        />
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={event.guest_tickets_required}
                                    onChange={e => handleEvent('guest_tickets_required', e)}
                                    color="primary"
                                    value="guest_tickets_required"
                                    disabled={!event.invitations_active}
                                />
                            }
                            label='Are non-group guests invited by group guests required to have tickets to this event? (i.e. "Non-Members")'
                            labelPlacement="end"
                            margin="normal"
                            className={classes.marginBottomSmall}
                        />
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={event.force_guest_ticket}
                                    onChange={e => handleEvent('force_guest_ticket', e)}
                                    color="primary"
                                    value="force_guest_ticket"
                                    disabled={!event.invitations_active}
                                />
                            }
                            label='Should we require group guests to select the ticket they send to non-group guests in the invitation? Otherwise non-group guests will be able to select from all available tickets for non-group guests.'
                            labelPlacement="end"
                            className={classes.marginBottomSmall}
                        />
                        <TextField
                            label="Number of Invitations allotted per Group Guest"
                            id="guests-allowed"
                            type="number"
                            value={event.guests_allowed}
                            onChange={e => handleEvent('guests_allowed', e)}
                            variant="outlined"
                            fullWidth
                            error={isError('guests_allowed')}
                            disabled={!event.invitations_active}
                            margin="normal"
                            helperText={getGuestsAllowedText()}
                        />
                    </div>
                </Collapse>
            </Grid>
        </Grid>
    )
}
